/**
 * ThemeProvider / useTheme — 전역 테마 소유권
 * ----------------------------------------------------------------------------
 * ★ 라이트 단일. 다크 모드를 만들지 마라.
 *   요청이 없으면 다크 토큰·미디어쿼리·토글을 추가하지 않는다. (CLAUDE.md)
 *
 * 이 파일은 그 정책의 **런타임 집행 지점**이다. `<html data-theme="light">` 를
 * 고정으로 세우고, 그 외의 값이 들어올 수 있는 경로(토글·setTheme·저장된 "dark")를
 * 전부 닫는다.
 *
 * 왜 타입까지 좁히나 — `Theme` 가 "dark" | "light" 인 동안에는 누구든 setTheme("dark")
 * 를 부활시킬 수 있고 컴파일이 통과한다. "light" 단일 타입으로 좁혀두면 다크 재도입
 * 시도가 기존 tsc 게이트에서 컴파일 에러로 걸린다(새 검사 장치 불필요).
 *
 * 역할 분담:
 * - public/index.html 인라인 스크립트: React 마운트 전 최초 1회 적용(FOUC 방지).
 * - 이 Provider: React 트리에 값 노출 + 마운트 시 dataset 재보장(독립 페이지 대비).
 *
 * 되돌리려면(다크 재도입이 결정되면) 이 파일과 public/index.html 의 인라인
 * 스크립트를 함께 되돌려야 한다. 한쪽만 바꾸면 FOUC 구간과 React 구간이 어긋난다.
 */
import React from "react";

/** ★ 라이트 단일 — 이 유니온에 "dark" 를 다시 넣지 마라. */
export type Theme = "light";

const THEME: Theme = "light";

/** 과거 다크 토글이 남긴 키. 고착 방지를 위해 정리 대상으로만 참조한다. */
const LEGACY_STORAGE_KEY = "itc-theme";

type ThemeApi = {
  theme: Theme;
};

const ThemeContext = React.createContext<ThemeApi | null>(null);

function applyTheme() {
  document.documentElement.dataset.theme = THEME;
}

/**
 * 예전에 "dark" 를 저장해 둔 사용자가 그 값에 고착되지 않도록 레거시 키를 제거한다.
 * 읽지 않고 지우기만 한다 — 저장값은 더 이상 테마를 결정하지 않는다.
 */
function clearLegacyThemeStorage() {
  try {
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    /* localStorage 불가 환경(프라이빗 모드·차단) 무시 */
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // 마운트 시 dataset 보장 (인라인 스크립트가 없거나 독립 페이지일 때 대비)
  React.useEffect(() => {
    clearLegacyThemeStorage();
    applyTheme();
  }, []);

  const api = React.useMemo<ThemeApi>(() => ({ theme: THEME }), []);

  return <ThemeContext.Provider value={api}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeApi {
  // Provider 미설치여도 값은 동일하다(라이트 단일). ctx 는 형식상 참조.
  return React.useContext(ThemeContext) ?? { theme: THEME };
}
